<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  task: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['updated', 'deleted', 'close']);

const store = useStore();
const { t } = useI18n();

const LABEL_COLORS = [
  '#61bd4f',
  '#f2d600',
  '#ff9f1a',
  '#eb5a46',
  '#c377e0',
  '#0079bf',
  '#00c2e0',
  '#51e898',
  '#ff78cb',
  '#344563',
];

const dialogRef = ref(null);

const title = ref('');
const description = ref('');
const assigneeId = ref('');
const dueDate = ref('');
const priority = ref('medium');
const status = ref('open');
const checklist = ref([]);
const labels = ref([]);
const coverColor = ref('');
const newItem = ref('');
const showLabelPalette = ref(false);

const agents = computed(() => store.getters['agents/getAgents'] || []);
const isDone = computed(() => status.value === 'done');

const checklistProgress = computed(() => {
  if (!checklist.value.length) return 0;
  const done = checklist.value.filter(item => item.done).length;
  return Math.round((done / checklist.value.length) * 100);
});
const progressBarStyle = computed(() => ({
  width: `${checklistProgress.value}%`,
  backgroundColor: coverColor.value || '#10b981',
}));
const progressLabel = computed(() => `${checklistProgress.value}%`);

const doneStyle = computed(() =>
  isDone.value ? { backgroundColor: '#10b981', borderColor: '#10b981' } : {}
);

const syncFromTask = () => {
  title.value = props.task.title || '';
  description.value = props.task.description || '';
  assigneeId.value = props.task.assignee_id || '';
  dueDate.value = props.task.due_date || '';
  priority.value = props.task.priority || 'medium';
  status.value = props.task.status || 'open';
  checklist.value = (props.task.checklist || []).map(item => ({ ...item }));
  labels.value = (props.task.labels || []).map(label => ({ ...label }));
  coverColor.value = props.task.cover_color || '';
};

watch(() => props.task, syncFromTask, { immediate: true });

onMounted(() => {
  store.dispatch('agents/get');
});

const save = async patch => {
  const updated = await store.dispatch('tasks/updateTask', {
    id: props.task.id,
    ...patch,
  });
  emit('updated', updated);
};

const saveTitle = () => {
  const value = title.value.trim();
  if (value && value !== props.task.title) save({ title: value });
};

const saveDescription = () => save({ description: description.value });
const saveAssignee = () => save({ assignee_id: assigneeId.value || null });
const saveDueDate = () => save({ due_date: dueDate.value || null });
const savePriority = () => save({ priority: priority.value });
const saveChecklist = () => save({ checklist: checklist.value });

const toggleDone = () => {
  status.value = isDone.value ? 'open' : 'done';
  save({ status: status.value });
};

const saveCover = () => save({ cover_color: coverColor.value || null });
const setCover = hex => {
  coverColor.value = coverColor.value === hex ? '' : hex;
  saveCover();
};
const clearCover = () => {
  coverColor.value = '';
  saveCover();
};

const saveLabels = () => save({ labels: labels.value });
const hasLabel = hex => labels.value.some(label => label.color === hex);
const addLabel = hex => {
  if (!hasLabel(hex)) labels.value.push({ color: hex, name: '' });
  showLabelPalette.value = false;
  saveLabels();
};
const removeLabel = index => {
  labels.value.splice(index, 1);
  saveLabels();
};

const fileInput = ref(null);
const isUploading = ref(false);
const isDragging = ref(false);
const attachments = computed(() => props.task.attachments || []);

const triggerUpload = () => fileInput.value?.click();

const uploadFiles = async files => {
  if (!files || !files.length) return;
  isUploading.value = true;
  try {
    const updated = await store.dispatch('tasks/addAttachment', {
      id: props.task.id,
      files,
    });
    emit('updated', updated);
  } finally {
    isUploading.value = false;
    if (fileInput.value) fileInput.value.value = '';
  }
};

const onFilesSelected = event => uploadFiles(event.target.files);

const onDrop = event => {
  isDragging.value = false;
  uploadFiles(event.dataTransfer?.files);
};

const removeAttachment = async attachmentId => {
  const updated = await store.dispatch('tasks/removeAttachment', {
    id: props.task.id,
    attachmentId,
  });
  emit('updated', updated);
};

// Links na tarefa (pedido Ivan 27/08: "onde sobe imagem também quero poder
// pôr o link") — moram em task.links (jsonb), salvos pelo update normal.
const taskLinks = computed(() => props.task.links || []);
const newLinkUrl = ref('');

const addLink = () => {
  let url = newLinkUrl.value.trim();
  if (!url) return;
  if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
  save({ links: [...taskLinks.value, { url, title: '' }] });
  newLinkUrl.value = '';
};

const removeLink = index => {
  save({ links: taskLinks.value.filter((_, i) => i !== index) });
};

const linkLabel = link => {
  if (link.title) return link.title;
  try {
    return (
      new URL(link.url).hostname.replace(/^www\./, '') +
      new URL(link.url).pathname.replace(/\/$/, '')
    );
  } catch {
    return link.url;
  }
};

const formatSize = bytes => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const showAllActivity = ref(false);
const activityList = computed(() =>
  (props.task.activities || []).slice().reverse()
);
const visibleActivities = computed(() =>
  showAllActivity.value ? activityList.value : activityList.value.slice(0, 2)
);

const activityText = entry => {
  const dash = '—';
  if (entry.type === 'assignee') {
    if (entry.from && entry.to) {
      return t('TASKS.ACTIVITY.REASSIGNED', { from: entry.from, to: entry.to });
    }
    if (entry.to) return t('TASKS.ACTIVITY.ASSIGNED', { to: entry.to });
    return t('TASKS.ACTIVITY.UNASSIGNED', { from: entry.from || dash });
  }
  if (entry.type === 'move') {
    return t('TASKS.ACTIVITY.MOVED', {
      from: entry.from || dash,
      to: entry.to || dash,
    });
  }
  if (entry.type === 'status') {
    return entry.to === 'done'
      ? t('TASKS.ACTIVITY.COMPLETED')
      : t('TASKS.ACTIVITY.REOPENED');
  }
  return '';
};

const relativeTime = at => {
  if (!at) return '';
  const diff = Math.max(0, Math.floor(Date.now() / 1000) - at);
  if (diff < 60) return t('TASKS.ACTIVITY.NOW');
  if (diff < 3600)
    return t('TASKS.ACTIVITY.MINUTES', { n: Math.floor(diff / 60) });
  if (diff < 86400)
    return t('TASKS.ACTIVITY.HOURS', { n: Math.floor(diff / 3600) });
  if (diff < 604800)
    return t('TASKS.ACTIVITY.DAYS', { n: Math.floor(diff / 86400) });
  return new Date(at * 1000).toLocaleDateString();
};

const toggleItem = item => {
  item.done = !item.done;
  saveChecklist();
};

const addItem = () => {
  const text = newItem.value.trim();
  if (!text) return;
  checklist.value.push({ id: crypto.randomUUID(), text, done: false });
  newItem.value = '';
  saveChecklist();
};

const removeItem = id => {
  checklist.value = checklist.value.filter(item => item.id !== id);
  saveChecklist();
};

const deleteTask = async () => {
  await store.dispatch('tasks/deleteTask', props.task.id);
  emit('deleted', props.task.id);
  dialogRef.value?.close();
};

const open = () => dialogRef.value?.open();
const close = () => dialogRef.value?.close();

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="xl"
    :show-confirm-button="false"
    :show-cancel-button="false"
    overflow-y-auto
    @close="emit('close')"
  >
    <div class="flex flex-col">
      <div
        v-if="coverColor"
        class="h-16 -mx-6 -mt-6 mb-5 rounded-t-xl"
        :style="{ backgroundColor: coverColor }"
      />

      <!-- Título + concluído -->
      <div class="flex items-start gap-3 mb-6">
        <button
          class="flex items-center justify-center mt-1 border-2 rounded-full size-6 shrink-0 transition-colors"
          :class="isDone ? '' : 'border-n-slate-7 hover:border-n-teal-9'"
          :style="doneStyle"
          :title="t('TASKS.CARD.MARK_DONE')"
          @click="toggleDone"
        >
          <span v-if="isDone" class="text-white i-lucide-check size-4" />
        </button>
        <input
          v-model="title"
          type="text"
          class="flex-1 min-w-0 px-2 py-1 text-lg font-semibold border border-transparent rounded-lg outline-none bg-transparent text-n-slate-12 hover:bg-n-alpha-1 focus:bg-n-alpha-black2 focus:border-n-weak"
          @blur="saveTitle"
          @keydown.enter.prevent="saveTitle"
        />
      </div>

      <!-- Responsável / Prazo / Prioridade -->
      <div class="grid grid-cols-1 gap-3 mb-6 sm:grid-cols-3">
        <div class="flex flex-col gap-1.5">
          <span
            class="text-[11px] font-semibold tracking-wide uppercase text-n-slate-10"
          >
            {{ t('TASKS.CARD.ASSIGNEE') }}
          </span>
          <select
            v-model="assigneeId"
            class="w-full px-2.5 py-2 text-sm border rounded-lg outline-none bg-n-alpha-black2 text-n-slate-12 border-n-weak focus:border-n-brand"
            @change="saveAssignee"
          >
            <option value="">{{ t('TASKS.CARD.UNASSIGNED') }}</option>
            <option v-for="agent in agents" :key="agent.id" :value="agent.id">
              {{ agent.name }}
            </option>
          </select>
        </div>
        <div class="flex flex-col gap-1.5">
          <span
            class="text-[11px] font-semibold tracking-wide uppercase text-n-slate-10"
          >
            {{ t('TASKS.CARD.DUE_DATE') }}
          </span>
          <input
            v-model="dueDate"
            type="date"
            class="w-full px-2.5 py-2 text-sm border rounded-lg outline-none bg-n-alpha-black2 text-n-slate-12 border-n-weak focus:border-n-brand"
            @change="saveDueDate"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <span
            class="text-[11px] font-semibold tracking-wide uppercase text-n-slate-10"
          >
            {{ t('TASKS.CARD.PRIORITY') }}
          </span>
          <select
            v-model="priority"
            class="w-full px-2.5 py-2 text-sm border rounded-lg outline-none bg-n-alpha-black2 text-n-slate-12 border-n-weak focus:border-n-brand"
            @change="savePriority"
          >
            <option value="low">{{ t('TASKS.PRIORITY.LOW') }}</option>
            <option value="medium">{{ t('TASKS.PRIORITY.MEDIUM') }}</option>
            <option value="high">{{ t('TASKS.PRIORITY.HIGH') }}</option>
          </select>
        </div>
      </div>

      <!-- Etiquetas -->
      <div class="flex flex-col gap-2 mb-6">
        <span
          class="text-[11px] font-semibold tracking-wide uppercase text-n-slate-10"
        >
          {{ t('TASKS.CARD.LABELS') }}
        </span>
        <div class="flex flex-wrap items-center gap-2">
          <div
            v-for="(label, index) in labels"
            :key="index"
            class="flex items-center gap-1.5 py-1 pl-2 pr-1 rounded-md bg-n-alpha-2"
          >
            <span
              class="rounded-full size-2.5 shrink-0"
              :style="{ backgroundColor: label.color }"
            />
            <input
              v-model="label.name"
              type="text"
              :placeholder="t('TASKS.LABEL_NAME_SHORT')"
              class="w-20 min-w-0 text-xs bg-transparent border-0 outline-none text-n-slate-12 placeholder:text-n-slate-9"
              @blur="saveLabels"
              @keydown.enter.prevent="saveLabels"
            />
            <button
              class="p-0.5 rounded text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-alpha-2"
              :title="t('TASKS.REMOVE')"
              @click="removeLabel(index)"
            >
              <span class="i-lucide-x size-3" />
            </button>
          </div>
          <button
            class="flex items-center gap-1 px-2 py-1 text-xs border border-dashed rounded-md text-n-slate-11 border-n-weak hover:border-n-brand hover:text-n-slate-12"
            @click="showLabelPalette = !showLabelPalette"
          >
            <span class="i-lucide-plus size-3" />
            {{ t('TASKS.ADD_LABEL') }}
          </button>
        </div>
        <div
          v-if="showLabelPalette"
          class="flex flex-wrap gap-1.5 p-2 rounded-lg bg-n-alpha-1"
        >
          <button
            v-for="hex in LABEL_COLORS"
            :key="hex"
            class="transition-transform rounded-md size-7 hover:scale-105"
            :class="hasLabel(hex) ? 'ring-2 ring-n-slate-12' : ''"
            :style="{ backgroundColor: hex }"
            @click="addLabel(hex)"
          />
        </div>
      </div>

      <!-- Capa -->
      <div class="flex flex-col gap-2 mb-6">
        <span
          class="text-[11px] font-semibold tracking-wide uppercase text-n-slate-10"
        >
          {{ t('TASKS.COVER') }}
        </span>
        <div class="flex flex-wrap items-center gap-1.5">
          <button
            v-for="hex in LABEL_COLORS"
            :key="hex"
            class="transition-transform rounded-md size-6 hover:scale-105"
            :class="coverColor === hex ? 'ring-2 ring-n-slate-12' : ''"
            :style="{ backgroundColor: hex }"
            @click="setCover(hex)"
          />
          <button
            v-if="coverColor"
            class="px-2 py-1 text-xs rounded-md text-n-slate-11 hover:bg-n-alpha-2"
            @click="clearCover"
          >
            {{ t('TASKS.NO_COVER') }}
          </button>
        </div>
      </div>

      <!-- Descrição -->
      <div class="flex flex-col gap-2 mb-6">
        <span
          class="text-[11px] font-semibold tracking-wide uppercase text-n-slate-10"
        >
          {{ t('TASKS.CARD.DESCRIPTION') }}
        </span>
        <textarea
          v-model="description"
          rows="3"
          :placeholder="t('TASKS.CARD.DESCRIPTION_PLACEHOLDER')"
          class="w-full px-3 py-2 text-sm border rounded-lg outline-none resize-none bg-n-alpha-black2 text-n-slate-12 border-n-weak focus:border-n-brand"
          @blur="saveDescription"
        />
      </div>

      <!-- Anexos -->
      <div class="flex flex-col gap-2 mb-6">
        <span
          class="text-[11px] font-semibold tracking-wide uppercase text-n-slate-10"
        >
          {{ t('TASKS.ATTACHMENTS') }}
        </span>

        <button
          type="button"
          class="flex flex-col items-center justify-center w-full gap-1 px-3 py-5 text-center transition-colors border-2 border-dashed rounded-lg cursor-pointer text-n-slate-11"
          :class="
            isDragging
              ? 'border-n-brand bg-n-brand/10'
              : 'border-n-weak hover:border-n-brand hover:bg-n-alpha-1'
          "
          :disabled="isUploading"
          @click="triggerUpload"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="onDrop"
        >
          <span
            :class="
              isUploading
                ? 'i-lucide-loader-circle animate-spin'
                : 'i-lucide-upload-cloud'
            "
            class="size-6"
          />
          <span class="text-sm font-medium text-n-slate-12">
            {{ isUploading ? t('TASKS.UPLOADING') : t('TASKS.DROP_HINT') }}
          </span>
          <span class="text-xs">{{ t('TASKS.DROP_TYPES') }}</span>
        </button>
        <input
          ref="fileInput"
          type="file"
          multiple
          class="hidden"
          @change="onFilesSelected"
        />

        <!-- Link como anexo -->
        <div class="flex items-center gap-2">
          <span class="i-lucide-link size-4 text-n-slate-10 shrink-0" />
          <input
            v-model="newLinkUrl"
            type="url"
            class="grow px-3 py-1.5 text-sm rounded-lg bg-n-alpha-black2 border border-n-weak text-n-slate-12 outline-none focus:border-n-brand"
            :placeholder="t('TASKS.LINK_PLACEHOLDER')"
            @keydown.enter.prevent="addLink"
          />
          <button
            class="px-3 py-1.5 text-sm font-medium rounded-lg bg-n-solid-3 text-n-slate-12 hover:bg-n-alpha-2 transition-colors disabled:opacity-50"
            :disabled="!newLinkUrl.trim()"
            @click="addLink"
          >
            {{ t('TASKS.LINK_ADD') }}
          </button>
        </div>
        <div v-if="taskLinks.length" class="flex flex-col gap-1.5">
          <div
            v-for="(link, index) in taskLinks"
            :key="`link-${index}`"
            class="flex items-center gap-2 px-3 py-2 border rounded-lg group bg-n-alpha-black2 border-n-weak"
          >
            <span
              class="i-lucide-external-link size-4 text-n-slate-10 shrink-0"
            />
            <a
              :href="link.url"
              target="_blank"
              rel="noopener noreferrer"
              class="text-sm truncate grow text-n-slate-12 hover:underline"
              :title="link.url"
            >
              {{ linkLabel(link) }}
            </a>
            <button
              class="p-1 transition-opacity rounded opacity-0 text-n-slate-10 hover:text-n-ruby-11 group-hover:opacity-100"
              :title="t('TASKS.REMOVE')"
              @click="removeLink(index)"
            >
              <span class="i-lucide-trash-2 size-4" />
            </button>
          </div>
        </div>

        <div v-if="attachments.length" class="flex flex-col gap-2">
          <div
            v-for="att in attachments"
            :key="att.id"
            class="flex flex-col overflow-hidden border rounded-lg group bg-n-alpha-black2 border-n-weak"
          >
            <a
              v-if="att.file_type === 'image'"
              :href="att.url"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                :src="att.url"
                :alt="att.filename"
                class="object-contain w-full bg-black/20 max-h-56"
              />
            </a>
            <video
              v-else-if="att.file_type === 'video'"
              :src="att.url"
              controls
              preload="metadata"
              class="w-full bg-black max-h-56"
            />

            <div class="flex items-center gap-2 p-2">
              <span
                :class="
                  att.file_type === 'image'
                    ? 'i-lucide-image'
                    : att.file_type === 'video'
                      ? 'i-lucide-film'
                      : att.file_type === 'audio'
                        ? 'i-lucide-music'
                        : 'i-lucide-file-text'
                "
                class="shrink-0 size-4 text-n-slate-10"
              />
              <div class="flex flex-col min-w-0 grow">
                <a
                  :href="att.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-sm truncate text-n-slate-12 hover:underline"
                >
                  {{ att.filename }}
                </a>
                <span class="text-xs text-n-slate-10">
                  {{ formatSize(att.byte_size) }}
                </span>
                <audio
                  v-if="att.file_type === 'audio'"
                  :src="att.url"
                  controls
                  class="w-full h-8 mt-1"
                />
              </div>
              <button
                class="p-1 transition-opacity rounded opacity-0 text-n-slate-10 hover:text-n-ruby-11 group-hover:opacity-100"
                :title="t('TASKS.REMOVE')"
                @click="removeAttachment(att.id)"
              >
                <span class="i-lucide-trash-2 size-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Checklist -->
      <div class="flex flex-col gap-2 mb-6">
        <div class="flex items-center justify-between">
          <span
            class="text-[11px] font-semibold tracking-wide uppercase text-n-slate-10"
          >
            {{ t('TASKS.CARD.CHECKLIST') }}
          </span>
          <span class="text-xs font-medium tabular-nums text-n-slate-11">
            {{ progressLabel }}
          </span>
        </div>
        <div class="w-full h-2 overflow-hidden rounded-full bg-n-alpha-2">
          <div
            class="h-full transition-all rounded-full"
            :style="progressBarStyle"
          />
        </div>
        <ul v-if="checklist.length" class="flex flex-col gap-0.5">
          <li
            v-for="item in checklist"
            :key="item.id"
            class="flex items-center gap-2 px-1 py-1 rounded group hover:bg-n-alpha-1"
          >
            <input
              type="checkbox"
              :checked="item.done"
              class="rounded size-4 accent-n-brand"
              @change="toggleItem(item)"
            />
            <span
              class="flex-1 text-sm"
              :class="
                item.done ? 'line-through text-n-slate-10' : 'text-n-slate-12'
              "
            >
              {{ item.text }}
            </span>
            <button
              class="transition-opacity opacity-0 text-n-slate-10 hover:text-n-ruby-11 group-hover:opacity-100"
              @click="removeItem(item.id)"
            >
              <span class="i-lucide-x size-4" />
            </button>
          </li>
        </ul>
        <form class="flex items-center gap-2" @submit.prevent="addItem">
          <input
            v-model="newItem"
            type="text"
            :placeholder="t('TASKS.CARD.ADD_ITEM')"
            class="flex-1 px-2.5 py-1.5 text-sm border rounded-lg outline-none bg-n-alpha-black2 text-n-slate-12 border-n-weak focus:border-n-brand"
          />
          <button
            type="submit"
            :disabled="!newItem.trim()"
            class="px-3 py-1.5 text-xs font-medium text-white rounded-md bg-n-brand hover:bg-n-brand/90 disabled:opacity-40"
          >
            {{ t('TASKS.CARD.ADD_ITEM') }}
          </button>
        </form>
      </div>

      <!-- Atividade / Histórico -->
      <div v-if="activityList.length" class="flex flex-col gap-2 mb-6">
        <span
          class="text-[11px] font-semibold tracking-wide uppercase text-n-slate-10"
        >
          {{ t('TASKS.ACTIVITY.TITLE') }}
        </span>
        <ul
          class="flex flex-col gap-2.5"
          :class="showAllActivity ? 'max-h-64 overflow-y-auto pr-1' : ''"
        >
          <li
            v-for="entry in visibleActivities"
            :key="entry.id"
            class="flex items-start gap-2"
          >
            <Avatar :name="entry.actor || '?'" :size="24" class="mt-0.5" />
            <div class="flex flex-col min-w-0">
              <p class="text-sm text-n-slate-12">
                <span class="font-medium">{{ entry.actor }}</span>
                {{ ' ' }}{{ activityText(entry) }}
              </p>
              <span class="text-xs text-n-slate-10">
                {{ relativeTime(entry.at) }}
              </span>
            </div>
          </li>
        </ul>
        <button
          v-if="activityList.length > 2"
          class="self-start text-xs font-medium text-n-brand hover:underline"
          @click="showAllActivity = !showAllActivity"
        >
          {{
            showAllActivity
              ? t('TASKS.ACTIVITY.SHOW_LESS')
              : t('TASKS.ACTIVITY.SHOW_ALL', { n: activityList.length })
          }}
        </button>
      </div>

      <!-- Rodapé -->
      <div
        class="flex items-center justify-between pt-4 border-t border-n-weak"
      >
        <button
          class="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg text-n-ruby-11 hover:bg-n-ruby-3"
          @click="deleteTask"
        >
          <span class="i-lucide-trash-2 size-4" />
          {{ t('TASKS.CARD.DELETE') }}
        </button>
        <button
          class="px-4 py-2 text-sm font-medium rounded-lg text-n-slate-12 bg-n-alpha-2 hover:bg-n-alpha-3"
          @click="close"
        >
          {{ t('TASKS.CLOSE') }}
        </button>
      </div>
    </div>
  </Dialog>
</template>
